import { AppContext } from "../core/context";
import { meshLibrary, productLibrary, seriesLibrary } from "../data";
import { Entity, Leg, Module, Product } from "../types/type_def";
import { ConversionHelper } from "../utils/conversion";
import { instantiateEntity } from "./entities";
import { createAttachmentPointRotated } from "./legs";

/**
 * Leg Helper
 *
 * Creates and removes legs on an entity. A leg consists of an attachment node
 * (`<entityNodeId>_attachment_<slot>`, tagged `leg <legType>`) with the leg entity/mesh as its child.
 */
export class LegHelper {

    /** Connection legs sit on the connection between two entities: no offset on the local X axis. */
    static readonly CONNECTION_LEG_TYPE = "lt02";

    /** Leg types that are part of the furniture model itself, so no leg mesh is loaded. */
    static readonly BUILT_IN_LEG_TYPES = ["lt06", "lt07", "lt09", "lt10"];

    static isConnectionLeg(legType: string): boolean {
        return legType.toLowerCase() === LegHelper.CONNECTION_LEG_TYPE;
    }

    static isBuiltInLeg(legType: string): boolean {
        return LegHelper.BUILT_IN_LEG_TYPES.includes(legType.toLowerCase());
    }

    /**
     * Creates the attachment and the leg for one slot (e.g. "left_front") of an entity.
     * An existing leg in the same slot is removed first; legs in other slots are not touched.
     * Returns the new attachment node.
     */
    static async createLeg(
        ctx: AppContext,
        module: Module,
        entity: Entity,
        slot: string,
        legType: string,
        position: BABYLON.Vector3,
        rotation: BABYLON.Vector3,
        scaling: BABYLON.Vector3,
        pivotNode?: BABYLON.TransformNode): Promise<BABYLON.TransformNode> {

        LegHelper.removeLeg(ctx, module, entity, slot);

        const entityNode = ctx.scene.getTransformNodeByUniqueId(entity.uniqueId);
        const attachmentNode = createAttachmentPointRotated(ctx, slot, `leg ${legType.toLowerCase()}`, entityNode, position, rotation, scaling, pivotNode);

        const entityProduct = productLibrary.getProductByProductNo(entity.productNo);
        const series = await seriesLibrary.fetchSeriesByID(entityProduct.series);
        const legProduct = seriesLibrary.getSeriesLeg(series, "standard", legType);

        if (legProduct && !LegHelper.isBuiltInLeg(legType)) {
            LegHelper.addLegToAttachment(ctx, module, entity, attachmentNode, legProduct, legType);
        }

        return attachmentNode;
    }

    /**
     * Removes the leg entity and its attachment node for one slot of an entity. Does nothing if there is none.
     */
    static removeLeg(ctx: AppContext, module: Module, entity: Entity, slot: string) {

        const entityNode = ctx.scene.getTransformNodeByUniqueId(entity.uniqueId);
        if (!entityNode) return;

        const attachmentId = `${entityNode.id}_attachment_${slot}`;
        const attachmentNode = entityNode.getChildTransformNodes(true, x => x.id === attachmentId)[0];
        if (!attachmentNode) return;

        // Leg entity nodes are named after their entity id
        const legEntityIds = attachmentNode
            .getChildTransformNodes(true, x => x.metadata && x.metadata.meshtype === "leg")
            .map(x => x.id);
        module.entities = module.entities.filter(x => !(x.parentId === entity.id && legEntityIds.includes(x.id)));

        // Disposes the leg node and its meshes as well
        attachmentNode.dispose(false);
    }

    /**
     * Instantiates the leg entity + mesh on an existing attachment node,
     * using the leg's offsets/rotation and the entity product's additional placement offsets.
     */
    static addLegToAttachment(ctx: AppContext, module: Module, entity: Entity, attachmentNode: BABYLON.TransformNode, legProduct: Leg, legType: string) {

        const entityNode = ctx.scene.getTransformNodeByUniqueId(entity.uniqueId);
        const entityProduct = productLibrary.getProductByProductNo(entity.productNo);
        const productVariant = productLibrary.getProductVariantByProductNo(entityProduct);

        let rotation = legProduct.legRotation;
        let offsetFront = ConversionHelper.scale(legProduct.legOffsetFromFront, ctx.scaleFactor) as number || 0;
        let offsetSide = ConversionHelper.scale(legProduct.legOffsetFromSide, ctx.scaleFactor) as number || 0;

        let additionalOffsetLeft = ConversionHelper.scale(productVariant.legPlacementAdditionalOffsetLeft, ctx.scaleFactor) as number || 0;
        let additionalOffsetRight = ConversionHelper.scale(productVariant.legPlacementAdditionalOffsetRight, ctx.scaleFactor) as number || 0;
        const additionalOffsetFront = ConversionHelper.scale(productVariant.legPlacementAdditionalOffsetFront, ctx.scaleFactor) as number || 0;
        const additionalOffsetBack = ConversionHelper.scale(productVariant.legPlacementAdditionalOffsetBack, ctx.scaleFactor) as number || 0;
        let additionalRotation = ConversionHelper.scale(productVariant.legPlacementFCIAdditionalAngle, ctx.scaleFactor) as number || 0;

        // Connection legs have no offset on the local X axis (side) and no extra angle
        if (LegHelper.isConnectionLeg(legType)) {
            offsetSide = 0;
            additionalOffsetLeft = 0;
            additionalOffsetRight = 0;
            additionalRotation = 0;
        }

        rotation += additionalRotation;

        switch (attachmentNode.id) {
            case entityNode.id + "_attachment_right_front":
            case entityNode.id + "_attachment_right_front_single":
            case entityNode.id + "_attachment_right_center": {
                offsetSide = -(offsetSide + additionalOffsetRight);
                offsetFront = -(offsetFront + additionalOffsetFront);
            }
                break;
            case entityNode.id + "_attachment_left_front":
            case entityNode.id + "_attachment_left_front_single":
            case entityNode.id + "_attachment_left_center": {
                offsetSide = -(offsetSide + additionalOffsetLeft);
                offsetFront = -(offsetFront + additionalOffsetFront);
            }
                break;
            case entityNode.id + "_attachment_right_back":
            case entityNode.id + "_attachment_right_back_single": {
                offsetSide = -(offsetSide + additionalOffsetRight);
                offsetFront = -(offsetFront + additionalOffsetBack);
            }
                break;
            case entityNode.id + "_attachment_left_back":
            case entityNode.id + "_attachment_left_back_single": {
                offsetSide = -(offsetSide + additionalOffsetLeft);
                offsetFront = -(offsetFront + additionalOffsetBack);
            }
                break;
        }

        const legProductVariant = {} as Product;
        legProductVariant.productNo = legProduct.productNumber;
        legProductVariant.width = legProductVariant.height = legProductVariant.depth = 0.01;
        legProductVariant.model = legProduct.model;
        legProductVariant.type = "LEG";

        instantiateEntity(ctx, module, legProductVariant, null, function (legEntity, legEntityNode) {

            const entityBounds = legEntityNode.getChildMeshes(true, function (m) { return m.id === "entitybounds" })[0];
            entityBounds.setEnabled(false);

            legEntity.parentId = entity.id;
            legEntityNode.parent = attachmentNode;
            legEntityNode.metadata = {};
            legEntityNode.metadata.productNo = legProductVariant.productNo;
            legEntityNode.metadata.meshtype = "leg";
            legEntityNode.rotation.addInPlace(new BABYLON.Vector3(0, BABYLON.Angle.FromDegrees(rotation).radians(), 0));
            legEntityNode.position.addInPlace(new BABYLON.Vector3(offsetSide, 0, offsetFront));

            meshLibrary.loadMeshAsync(legProductVariant.productNo, legEntity, "leg", "legs/", legProductVariant.model + ".glb", false, ctx.scene, true, ctx.sg, ctx.sg2, null, function (clonedMeshEntityId, clonedMesh) {
                clonedMesh.isPickable = false;
                clonedMesh.getChildMeshes(false).forEach(function (subMesh) {
                    subMesh.isPickable = false;
                });
            });
        });
    }
}
